// Intel HUD readout + real-time broadcast panel (the primary notification
// channel — works with no push permission).
import { el } from "../lib/dom";
import { watchBroadcasts, type Broadcast } from "../lib/data";
import { icon } from "./icons";

export function hud(opts: {
  intel: number;
  onBell: () => void;
  unread: number;
  onRanks?: () => void;
  onHelp?: () => void;
  onFeed?: () => void;
}): HTMLElement {
  const bell = el("button.hud__bell", { onclick: opts.onBell }, icon("bell", 20));
  if (opts.unread > 0) bell.append(el("span.badge", {}, String(Math.min(opts.unread, 99))));
  const right = el("div.row", { style: "gap:2px" },
    opts.onHelp
      ? el("button.hud__bell", {
          onclick: opts.onHelp,
          title: "Mission briefing",
          style: "font-size:14px;font-family:var(--mono);font-weight:700",
        }, "?")
      : null,
    opts.onFeed
      ? el("button.hud__bell", { onclick: opts.onFeed, title: "Surveillance feed" }, icon("proof", 20))
      : null,
    opts.onRanks
      ? el("button.hud__bell", { onclick: opts.onRanks, title: "Agent rankings" }, icon("ranks", 20))
      : null,
    bell,
  );
  return el(
    "div.hud",
    {},
    el("div", {}, el("span.tiny.dim", {}, "INTEL "), el("span.hud__intel", {}, `${opts.intel} ◆`)),
    right,
  );
}

const READ_KEY = "rg_broadcasts_read";

export function broadcastPanel(_uid: string) {
  let broadcasts: Broadcast[] = [];
  let open = false;
  let changeCb: (() => void) | null = null;
  const panel = el("div.card", { style: "display:none" });

  function readIds(): Set<string> {
    try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) ?? "[]")); }
    catch { return new Set(); }
  }
  function persist(ids: Set<string>) {
    localStorage.setItem(READ_KEY, JSON.stringify([...ids]));
  }
  function markRead(id: string) {
    const ids = readIds();
    ids.add(id);
    persist(ids);
    render();
    changeCb?.();
  }
  function markAllRead() {
    const ids = readIds();
    broadcasts.forEach((b) => ids.add(b.id));
    persist(ids);
    render();
    changeCb?.();
  }
  function unread(): number {
    const ids = readIds();
    return broadcasts.filter((b) => !ids.has(b.id)).length;
  }

  function row(b: Broadcast): HTMLElement {
    const isRead = readIds().has(b.id);
    const ts = b.createdAt?.toMillis?.();
    const readBtn = el("button.btn--ghost.btn.btn--sm", {
      style: "font-size:10px;padding:4px 8px;flex:none",
      onclick: () => markRead(b.id),
    }, "Mark read");
    return el("div", {
      style: `border-top:1px solid var(--line); padding:10px 0; ${isRead ? "opacity:0.45" : ""}`,
    },
      el("div.row", { style: "justify-content:space-between;align-items:flex-start;gap:8px" },
        el("strong.mono.amber", {}, b.title || "CONTROL"),
        isRead ? el("span.tiny.dim", { style: "flex:none" }, "read") : readBtn,
      ),
      el("p", { style: "margin:4px 0 0" }, b.body),
      ts ? el("p.tiny.dim.mono", { style: "margin:4px 0 0" }, new Date(ts).toLocaleString()) : null,
    );
  }

  function render() {
    panel.style.display = open ? "block" : "none";
    if (!open) return;
    const header = el("div.row", { style: "justify-content:space-between;align-items:center" },
      el("span.card__tag", {}, "SECURE TRANSMISSIONS"),
      unread() > 0
        ? el("button.btn--ghost.btn.btn--sm", {
            style: "font-size:10px;padding:4px 8px;flex:none;margin-bottom:10px",
            onclick: markAllRead,
          }, "Mark all read")
        : null,
    );
    panel.replaceChildren(
      header,
      ...(broadcasts.length
        ? broadcasts.slice(0, 30).map(row)
        : [el("p.tiny.dim", {}, "No transmissions yet. Stand by.")]),
    );
  }

  watchBroadcasts((b) => { broadcasts = b; render(); changeCb?.(); });

  return {
    el: panel,
    toggle() {
      open = !open;
      render();
      changeCb?.();
    },
    unread,
    onChange(cb: () => void) { changeCb = cb; return () => { changeCb = null; }; },
  };
}
